// Referee match-report forms — the printable, fill-in-by-hand record of one
// football match (see `features/football-tournaments.md`). The organizer
// prints a form per match and hands it to the referee, who records the final
// score, scorers, cards and (for a knock-out draw) the penalty shoot-out, then
// signs it together with both captains.
//
// Like `scoresheet.ts` this module is pure data: it turns a `TournamentState`
// into a framework-free model describing what belongs on each form. Rendering
// (HTML/print) lives in the UI layer (`src/ui/refereeFormDocument.ts`).

import type {
  Cell,
  FootballCardColor,
  FootballMatchEvent,
  PlayerId,
  TournamentState,
} from './types';
import { timeSlotLabel } from './ids';
import { groupMatchOrder, isBracketMatchPrintable } from './scoresheet';
import { isCardEvent, isGoalEvent, resolveFootballEvents } from './football';

/** A team as printed on a referee form: name plus its squad member list. */
export interface RefereeFormTeam {
  id: PlayerId; // "P1" — per-tournament entry slot
  seed: number; // 1-based global entry index
  name: string; // team name, falling back to the id
  members: string[]; // squad names in list order (may be empty)
}

/** A recorded goal as one pre-filled row of the printed goals grid. */
export interface RefereeFormGoalRow {
  minute: number | null; // match minute; null prints an empty minute cell
  teamName: string; // the team the goal counts FOR
  scorerNumber: number; // the scorer's 1-based squad-list position
  scorerName: string; // resolved scorer name ('' when the roster lost the entry)
  ownGoal: boolean; // the scorer belongs to the other team
}

/** A recorded booking as one pre-filled row of the printed cards grid. */
export interface RefereeFormCardRow {
  minute: number | null; // match minute; null prints an empty minute cell
  teamName: string; // the booked player's team
  playerNumber: number; // the player's 1-based squad-list position
  playerName: string; // resolved player name ('' when the roster lost the entry)
  card: FootballCardColor;
}

/** Everything needed to print one match's referee report form. */
export interface RefereeMatchForm {
  tournamentId: string;
  tournamentName: string;
  matchId: string; // Cell_PoolA_P1_P3 or Node_R1_M1
  kind: 'group' | 'bracket';
  poolLabel: string | null; // "A" for a group match, null for bracket
  matchOrder: number | null; // 1-based play order within the group, null for bracket
  nodeLabel: string | null; // "Quarterfinal 1" for bracket, null for group
  timeSlot: number | null; // 1-based Time_Slot index; null for bracket matches
  timeSlotLabel: string | null; // "Time_Slot_1"; null for bracket matches
  tableIndex: number | null; // 1-based pitch within the time slot; null for bracket
  teamA: RefereeFormTeam;
  teamB: RefereeFormTeam;
  refereeName: string | null; // assigned referee's name; null prints a blank line
  hasShootoutSection: boolean; // knock-out forms carry a penalty shoot-out grid
  // The match's recorded events, projected into printable grid rows (entry
  // order). Empty for an unrecorded match, whose grids print blank as before;
  // a recorded match re-prints as a filled-in report of record.
  goals: RefereeFormGoalRow[];
  cards: RefereeFormCardRow[];
}

/**
 * Projects a match's recorded events into pre-filled goals/cards grid rows,
 * resolving each member reference against the two squad lists.
 */
function toEventRows(
  events: readonly FootballMatchEvent[],
  teamA: RefereeFormTeam,
  teamB: RefereeFormTeam,
): { goals: RefereeFormGoalRow[]; cards: RefereeFormCardRow[] } {
  const resolved = resolveFootballEvents(events, teamA.members, teamB.members);
  const teamName = (side: 'A' | 'B') =>
    side === 'A' ? teamA.name : teamB.name;
  const goals: RefereeFormGoalRow[] = [];
  const cards: RefereeFormCardRow[] = [];
  for (const { event, memberNumber, memberName } of resolved) {
    if (isGoalEvent(event)) {
      goals.push({
        minute: event.minute,
        teamName: teamName(event.side),
        scorerNumber: memberNumber,
        scorerName: memberName,
        ownGoal: event.ownGoal,
      });
    } else if (isCardEvent(event)) {
      cards.push({
        minute: event.minute,
        teamName: teamName(event.side),
        playerNumber: memberNumber,
        playerName: memberName,
        card: event.card,
      });
    }
  }
  return { goals, cards };
}

/** Team entry for a form, falling back to the bare id for a missing record. */
function toFormTeam(tournament: TournamentState, id: PlayerId): RefereeFormTeam {
  const player = tournament.players[id];
  const digits = /(\d+)/.exec(id);
  return {
    id,
    seed: player?.seed ?? (digits ? Number(digits[1]) : Number.MAX_SAFE_INTEGER),
    name: player?.name ?? id,
    members: player?.members ?? [],
  };
}

/** The assigned referee's name, or null when unassigned/unknown. */
function refereeNameOf(
  tournament: TournamentState,
  refereeId: string | null,
): string | null {
  if (refereeId === null) return null;
  return tournament.referees[refereeId]?.name ?? null;
}

/**
 * Builds the referee form for a single group-stage match. Throws if the cell
 * id is unknown — callers should pass ids reachable from `tournament.groups`.
 */
export function buildCellRefereeForm(
  tournament: TournamentState,
  cellId: string,
): RefereeMatchForm {
  const cell = tournament.cells[cellId];
  if (!cell) {
    throw new Error(`Unknown match: ${cellId}`);
  }
  const teamA = toFormTeam(tournament, cell.playerA);
  const teamB = toFormTeam(tournament, cell.playerB);
  return {
    tournamentId: tournament.id,
    tournamentName: tournament.name,
    matchId: cell.id,
    kind: 'group',
    poolLabel: cell.poolLabel,
    matchOrder: groupMatchOrder(tournament, cell),
    nodeLabel: null,
    timeSlot: cell.timeSlot,
    timeSlotLabel: timeSlotLabel(cell.timeSlot),
    tableIndex: cell.tableIndex,
    teamA,
    teamB,
    refereeName: refereeNameOf(tournament, cell.refereeId),
    hasShootoutSection: false,
    ...toEventRows(cell.events, teamA, teamB),
  };
}

/**
 * Builds the referee form for a single bracket match. Throws if the node id is
 * unknown or either participant is still unresolved (a form headed "Winner of
 * Semifinal 1" cannot collect a result) — gate on `isBracketMatchPrintable`,
 * exactly as the score sheets do.
 */
export function buildNodeRefereeForm(
  tournament: TournamentState,
  nodeId: string,
): RefereeMatchForm {
  const node = tournament.nodes[nodeId];
  if (!node) {
    throw new Error(`Unknown bracket node: ${nodeId}`);
  }
  if (!isBracketMatchPrintable(node)) {
    throw new Error(
      `Bracket node ${nodeId} has unresolved teams; nothing to print yet.`,
    );
  }
  const teamA = toFormTeam(tournament, node.playerA);
  const teamB = toFormTeam(tournament, node.playerB);
  return {
    tournamentId: tournament.id,
    tournamentName: tournament.name,
    matchId: node.id,
    kind: 'bracket',
    poolLabel: null,
    matchOrder: null,
    nodeLabel: node.label,
    timeSlot: null,
    timeSlotLabel: null,
    tableIndex: null,
    teamA,
    teamB,
    refereeName: refereeNameOf(tournament, node.refereeId),
    hasShootoutSection: true,
    ...toEventRows(node.events, teamA, teamB),
  };
}

/** Orders cells the way they are actually played: time slot, then pitch. */
function byPlayOrder(a: Cell, b: Cell): number {
  if (a.timeSlot !== b.timeSlot) return a.timeSlot - b.timeSlot;
  return a.tableIndex - b.tableIndex;
}

/**
 * Builds a referee form for every match of one pool, in play order. Throws if
 * the pool id is unknown — callers should pass ids from `groupOrder`.
 */
export function buildGroupRefereeForms(
  tournament: TournamentState,
  poolId: string,
): RefereeMatchForm[] {
  const group = tournament.groups[poolId];
  if (!group) {
    throw new Error(`Unknown pool: ${poolId}`);
  }
  return group.cellIds
    .map((cellId): Cell | undefined => tournament.cells[cellId])
    .filter((cell): cell is Cell => cell !== undefined)
    .sort(byPlayOrder)
    .map((cell) => buildCellRefereeForm(tournament, cell.id));
}

/**
 * Builds a referee form for every group-stage match (all pools in display
 * order, matches in play order) followed by every bracket match whose teams
 * are already resolved.
 */
export function buildAllRefereeForms(
  tournament: TournamentState,
): RefereeMatchForm[] {
  const groupForms = tournament.groupOrder.flatMap((poolId) =>
    buildGroupRefereeForms(tournament, poolId),
  );
  const bracketForms = tournament.nodeOrder
    .map((nodeId) => tournament.nodes[nodeId])
    .filter((node) => node !== undefined && isBracketMatchPrintable(node))
    .map((node) => buildNodeRefereeForm(tournament, node.id));
  return [...groupForms, ...bracketForms];
}
